"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
/**
 * UserManagementPage - List, create, edit, and delete app users
 */
const react_1 = require("react");
const react_table_1 = require("@tanstack/react-table");
const tenants_1 = require("../constants/tenants");
const userApi_1 = require("../helpers/userApi");
const roleApi_1 = require("../helpers/roleApi");
const ToastProvider_1 = require("../providers/ToastProvider");
const components_1 = require("../components");
const UserFormModal_1 = __importDefault(require("../components/UserFormModal"));
require("./UserManagementPage.scss");
const columnHelper = (0, react_table_1.createColumnHelper)();
const tenantNameById = Object.fromEntries(tenants_1.TENANTS.map(t => [t.id, t.name]));
/**
 * Null-safe case-insensitive substring filter.
 *
 * TanStack's built-in `includesString` does `value?.toString().toLowerCase()`
 * which returns `undefined` for null values and then throws a TypeError on the
 * chained `.toLowerCase()` call. TanStack swallows the error and lets every row
 * pass, making the filter appear to have no effect on columns where the API
 * returns null (e.g. `name`).
 */
const nullSafeIncludesString = (row, columnId, filterValue) => {
    const value = row.getValue(columnId);
    if (value == null)
        return false;
    return String(value).toLowerCase().includes(filterValue.toLowerCase());
};
nullSafeIncludesString.autoRemove = (val) => !val;
/**
 * Filter input with a <datalist> of unique values for the column.
 * Suggestions are drawn from getFacetedUniqueValues(), which reflects the rows
 * that survive all OTHER columns' active filters — so the options cascade.
 */
const ColumnFilter = ({ column, headerId }) => {
    var _a;
    const label = typeof column.columnDef.header === 'string' ? column.columnDef.header : headerId;
    const sortedUniqueValues = (0, react_1.useMemo)(() => Array.from(column.getFacetedUniqueValues().keys())
        .filter((v) => v != null && v !== '')
        .sort(), 
    // getFacetedUniqueValues returns a new Map reference whenever the faceted
    // model recomputes, so spreading it into the dep array is intentional.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [column.getFacetedUniqueValues()]);
    const listId = `col-filter-${headerId}`;
    return ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("datalist", { id: listId, children: sortedUniqueValues.map(val => ((0, jsx_runtime_1.jsx)("option", { value: val }, val))) }), (0, jsx_runtime_1.jsx)("input", { className: "user-management-page__filter-input", list: listId, value: (_a = column.getFilterValue()) !== null && _a !== void 0 ? _a : '', onChange: e => column.setFilterValue(e.target.value), placeholder: "Filter...", "aria-label": `Filter by ${label}` })] }));
};
const UserManagementPage = () => {
    const toast = (0, ToastProvider_1.useToast)();
    const [users, setUsers] = (0, react_1.useState)([]);
    const [roles, setRoles] = (0, react_1.useState)([]);
    const [loading, setLoading] = (0, react_1.useState)(true);
    const [fetchError, setFetchError] = (0, react_1.useState)('');
    const [sorting, setSorting] = (0, react_1.useState)([]);
    const [columnFilters, setColumnFilters] = (0, react_1.useState)([]);
    const [modal, setModal] = (0, react_1.useState)(null);
    const loadUsers = () => __awaiter(void 0, void 0, void 0, function* () {
        setFetchError('');
        setLoading(true);
        try {
            const data = yield userApi_1.userApi.getAll();
            setUsers(data);
        }
        catch (err) {
            const msg = err instanceof Error ? err.message : 'Failed to load users.';
            setFetchError(msg);
        }
        finally {
            setLoading(false);
        }
    });
    (0, react_1.useEffect)(() => {
        loadUsers();
        roleApi_1.roleApi.getAll().then(setRoles).catch(() => { });
    }, []);
    const columns = (0, react_1.useMemo)(() => [
        columnHelper.accessor('name', {
            header: 'Name',
            enableSorting: true,
            enableColumnFilter: true,
            filterFn: nullSafeIncludesString,
            cell: info => info.getValue() || '\u2014',
        }),
        columnHelper.accessor('username', {
            header: 'Email',
            enableSorting: true,
            enableColumnFilter: true,
            filterFn: nullSafeIncludesString,
            cell: info => info.getValue(),
        }),
        columnHelper.accessor('profileId', {
            header: 'Profile ID',
            enableSorting: true,
            enableColumnFilter: true,
            filterFn: nullSafeIncludesString,
            cell: info => info.getValue() || '\u2014',
        }),
        columnHelper.accessor('roles', {
            header: 'Roles',
            enableSorting: true,
            enableColumnFilter: true,
            filterFn: nullSafeIncludesString,
            cell: info => info.getValue() || '\u2014',
        }),
        columnHelper.accessor('type', {
            header: 'Type',
            enableSorting: true,
            enableColumnFilter: true,
            filterFn: nullSafeIncludesString,
            cell: info => info.getValue() || '\u2014',
        }),
        // Use accessorFn so filter/sort operate on the display name, not the raw UUID
        columnHelper.accessor(row => { var _a; return (row.tenantId ? (_a = tenantNameById[row.tenantId]) !== null && _a !== void 0 ? _a : row.tenantId : ''); }, {
            id: 'tenant',
            header: 'Tenant',
            enableSorting: true,
            enableColumnFilter: true,
            filterFn: nullSafeIncludesString,
            cell: info => info.getValue() || '\u2014',
        }),
        columnHelper.display({
            id: 'actions',
            header: 'Actions',
            enableColumnFilter: false,
            cell: ({ row }) => ((0, jsx_runtime_1.jsxs)("div", { className: "user-management-page__table-actions", children: [(0, jsx_runtime_1.jsx)(components_1.Button, { size: "small", variant: "ghost", onClick: () => setModal({ mode: 'edit', user: row.original }), "aria-label": `Edit ${row.original.username}`, children: "Edit" }), (0, jsx_runtime_1.jsx)(components_1.Button, { size: "small", variant: "danger", onClick: () => setModal({ mode: 'delete', user: row.original }), "aria-label": `Delete ${row.original.username}`, children: "Delete" })] })),
        }),
    ], []);
    const table = (0, react_table_1.useReactTable)({
        data: users,
        columns,
        state: { sorting, columnFilters },
        onSortingChange: setSorting,
        onColumnFiltersChange: setColumnFilters,
        getCoreRowModel: (0, react_table_1.getCoreRowModel)(),
        getSortedRowModel: (0, react_table_1.getSortedRowModel)(),
        getFilteredRowModel: (0, react_table_1.getFilteredRowModel)(),
        getFacetedRowModel: (0, react_table_1.getFacetedRowModel)(),
        getFacetedUniqueValues: (0, react_table_1.getFacetedUniqueValues)(),
    });
    const handleModalSuccess = (action) => {
        setModal(null);
        toast.success(`User ${action} successfully.`);
        loadUsers();
    };
    const filteredCount = table.getRowModel().rows.length;
    const totalCount = users.length;
    const isFiltered = columnFilters.length > 0;
    return ((0, jsx_runtime_1.jsxs)("div", { className: "user-management-page", children: [(0, jsx_runtime_1.jsxs)("div", { className: "user-management-page__header", children: [(0, jsx_runtime_1.jsxs)("div", { className: "user-management-page__header-content", children: [(0, jsx_runtime_1.jsx)("h1", { className: "user-management-page__title", children: "User Management" }), (0, jsx_runtime_1.jsx)("p", { className: "user-management-page__subtitle", children: "Create, edit, and delete application users." })] }), (0, jsx_runtime_1.jsx)(components_1.Button, { variant: "primary", onClick: () => setModal({ mode: 'create' }), children: "Add User" })] }), (0, jsx_runtime_1.jsxs)("div", { className: "user-management-page__content", children: [loading && ((0, jsx_runtime_1.jsx)("div", { className: "user-management-page__loading", children: (0, jsx_runtime_1.jsx)(components_1.LoadingSpinner, { label: "Loading users..." }) })), !loading && fetchError && ((0, jsx_runtime_1.jsx)("div", { className: "user-management-page__error", role: "alert", children: (0, jsx_runtime_1.jsx)("p", { children: fetchError }) })), !loading && !fetchError && users.length === 0 && ((0, jsx_runtime_1.jsx)("div", { className: "user-management-page__empty", children: (0, jsx_runtime_1.jsx)("p", { children: "No users found." }) })), !loading && !fetchError && users.length > 0 && ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [isFiltered && ((0, jsx_runtime_1.jsxs)("div", { className: "user-management-page__filter-summary", children: ["Showing ", filteredCount, " of ", totalCount, " users", (0, jsx_runtime_1.jsx)("button", { className: "user-management-page__clear-filters", onClick: () => setColumnFilters([]), children: "Clear filters" })] })), (0, jsx_runtime_1.jsx)("div", { className: "user-management-page__table-wrapper", children: (0, jsx_runtime_1.jsxs)("table", { className: "user-management-page__table", children: [(0, jsx_runtime_1.jsx)("thead", { children: table.getHeaderGroups().map(headerGroup => ((0, jsx_runtime_1.jsx)("tr", { children: headerGroup.headers.map(header => ((0, jsx_runtime_1.jsxs)("th", { className: header.column.getCanSort() ? 'user-management-page__th--sortable' : '', children: [(0, jsx_runtime_1.jsxs)("div", { className: "user-management-page__th-label", onClick: header.column.getToggleSortingHandler(), style: { cursor: header.column.getCanSort() ? 'pointer' : 'default' }, children: [header.isPlaceholder
                                                                    ? null
                                                                    : (0, react_table_1.flexRender)(header.column.columnDef.header, header.getContext()), header.column.getIsSorted() === 'asc' && ' \u25B2', header.column.getIsSorted() === 'desc' && ' \u25BC'] }), header.column.getCanFilter() && ((0, jsx_runtime_1.jsx)(ColumnFilter, { column: header.column, headerId: header.id }))] }, header.id))) }, headerGroup.id))) }), (0, jsx_runtime_1.jsx)("tbody", { children: filteredCount === 0 ? ((0, jsx_runtime_1.jsx)("tr", { children: (0, jsx_runtime_1.jsx)("td", { colSpan: columns.length, className: "user-management-page__no-results", children: "No users match the current filters." }) })) : (table.getRowModel().rows.map(row => ((0, jsx_runtime_1.jsx)("tr", { children: row.getVisibleCells().map(cell => ((0, jsx_runtime_1.jsx)("td", { children: (0, react_table_1.flexRender)(cell.column.columnDef.cell, cell.getContext()) }, cell.id))) }, row.id)))) })] }) })] }))] }), (modal === null || modal === void 0 ? void 0 : modal.mode) === 'create' && ((0, jsx_runtime_1.jsx)(UserFormModal_1.default, { mode: "create", roles: roles, onSuccess: () => handleModalSuccess('created'), onClose: () => setModal(null) })), (modal === null || modal === void 0 ? void 0 : modal.mode) === 'edit' && ((0, jsx_runtime_1.jsx)(UserFormModal_1.default, { mode: "edit", user: modal.user, roles: roles, onSuccess: () => handleModalSuccess('updated'), onClose: () => setModal(null) })), (modal === null || modal === void 0 ? void 0 : modal.mode) === 'delete' && ((0, jsx_runtime_1.jsx)(UserFormModal_1.default, { mode: "delete", user: modal.user, roles: roles, onSuccess: () => handleModalSuccess('deleted'), onClose: () => setModal(null) }))] }));
};
exports.default = UserManagementPage;
